/**
 * Order Draft — canonical "Prep Order" model.
 *
 * Two-stage ordering: Prep (safe, reversible, no spend) → Push (vendor submission).
 *
 * Core principle: an Order Draft is NOT a free-form document. It is a
 * deterministic projection of the JobSpec BOM (or fallback materialsList)
 * captured at the moment of Prep. The only legitimate way the underlying
 * BOM can change after Prep is via a Change Order. Anything else is a bug.
 *
 * Drift classification at Push time:
 *   • Hash matches              → push proceeds
 *   • Hash differs + Change Order signed since prep → lawful drift; draft
 *     should have already been auto-voided when the CO was signed and a
 *     fresh prep is required
 *   • Hash differs + no Signed CO since prep → INTEGRITY_VIOLATION (bug):
 *     block push, void draft, write Timeline event + FrictionLog entry
 *
 * Pure helpers — no React, no side effects. Context layer wires lifecycle.
 */

import type { Job } from "./types";
import type { ChangeOrder } from "./changeOrderTypes";
import { resolveBoundSpec } from "./jobSpec";
import { derivePreSpendGate, type PreSpendGate } from "./preSpendGate";

// ── Types ─────────────────────────────────────────────────────────────────

export interface OrderDraftLineItem {
  sku: string;
  productName: string;
  quantity: number;
  vendorName: string;
}

export interface OrderDraftVendorGroup {
  vendorId: string;
  vendorName: string;
  items: OrderDraftLineItem[];
  /** Total cost is captured at prep time but only surfaced to roles allowed to see pricing. */
  totalCost: number;
}

export type OrderDraftStatus = "prepared" | "pushed" | "voided";
export type OrderDraftVoidReason = "change_order" | "integrity_violation" | "manual";

export interface OrderDraft {
  draftId: string;
  jobId: string;
  /** Snapshot of the BOM hash at prep time. Re-computed at push for drift check. */
  sourceBomHash: string;
  /** Spec ref pinned at prep — e.g. "TTS-STD-60x32-v1" or "legacy:materialsList". */
  sourceSpecRef: string;
  vendorGroups: OrderDraftVendorGroup[];
  notes?: string;
  preparedAt: string;
  preparedBy: string;
  status: OrderDraftStatus;
  pushedAt?: string;
  pushedBy?: string;
  /** PO IDs created on Push — backlinks for audit. */
  pushedPoIds?: string[];
  voidedAt?: string;
  voidedReason?: OrderDraftVoidReason;
  voidedChangeOrderId?: string;
  /** ISO timestamp of the most recent signed Change Order known at prep. */
  lastSignedChangeOrderAtPrep?: string;
}

// ── BOM hashing ───────────────────────────────────────────────────────────

/**
 * Stable, order-independent hash of a job's effective BOM.
 *
 * Read order:
 *  1. Bound JobSpec snapshot (canonical) — preferred when present.
 *  2. job.materialsList — legacy fallback.
 *
 * The hash is a simple djb2 variant computed over a normalized
 * "sku:qty" stringification with items sorted by sku. Deterministic,
 * collision-resistant enough for drift detection (not a security primitive).
 */
export function computeBomHash(job: Job): string {
  const items = collectBomItems(job);
  const normalized = items
    .map((i) => `${i.sku}:${i.quantity}`)
    .sort()
    .join("|");

  let h = 5381;
  for (let i = 0; i < normalized.length; i++) {
    h = ((h << 5) + h + normalized.charCodeAt(i)) | 0;
  }
  // Render as unsigned hex with a length tag so it's grep-able in logs.
  return `bom_${(h >>> 0).toString(16)}_${items.length}`;
}

interface NormalizedBomItem { sku: string; name: string; quantity: number; }

export function collectBomItems(job: Job): NormalizedBomItem[] {
  const bound = job.boundJobSpec;
  if (bound) {
    const spec = resolveBoundSpec(bound);
    if (spec?.billOfMaterials?.length) {
      return spec.billOfMaterials.map((b) => ({
        sku: b.sku,
        name: b.name,
        quantity: b.quantity,
      }));
    }
  }
  const ml = job.materialsList || [];
  return ml
    .filter((m) => !!(m.sku || m.itemName))
    .map((m) => ({
      sku: m.sku || m.itemName,
      name: m.itemName,
      quantity: m.quantityRequired || 1,
    }));
}

export function getSpecRef(job: Job): string {
  const b = job.boundJobSpec;
  if (b) return `${b.specId}-v${b.versionNumber}`;
  return "legacy:materialsList";
}

// ── Change Order coupling ─────────────────────────────────────────────────

/** Most recent signed CO timestamp, or undefined if none signed. */
export function getLastSignedChangeOrderAt(job: Job): string | undefined {
  const cos = job.changeOrders || [];
  const signed = cos
    .filter((c): c is ChangeOrder & { signedAt: string } => c.status === "Signed" && !!c.signedAt)
    .map((c) => c.signedAt);
  if (signed.length === 0) return undefined;
  return signed.sort().slice(-1)[0];
}

/** Has any CO been signed strictly after the given prep timestamp? */
export function hasSignedChangeOrderAfter(job: Job, isoTimestamp: string): boolean {
  const last = getLastSignedChangeOrderAt(job);
  if (!last) return false;
  return last > isoTimestamp;
}

// ── Push validation ───────────────────────────────────────────────────────

export type PushBlockReason =
  | { kind: "no_draft" }
  | { kind: "draft_voided"; reason: OrderDraftVoidReason }
  | { kind: "gate_blocked"; gate: PreSpendGate }
  | { kind: "lawful_drift_change_order"; changeOrderSignedAt: string }
  | { kind: "integrity_violation"; expectedHash: string; actualHash: string };

export interface PushValidation {
  ok: boolean;
  reason?: PushBlockReason;
}

/**
 * Validate a draft for Push. Re-checks:
 *  1. Draft exists and is in `prepared` state.
 *  2. All pre-spend gates still cleared at push time.
 *  3. BOM hash unchanged since prep — else classify drift.
 */
export function validateDraftForPush(draft: OrderDraft | undefined, job: Job): PushValidation {
  if (!draft) return { ok: false, reason: { kind: "no_draft" } };
  if (draft.status === "voided") {
    return { ok: false, reason: { kind: "draft_voided", reason: draft.voidedReason || "manual" } };
  }
  if (draft.status === "pushed") {
    // Already pushed — treat as no-op for the caller.
    return { ok: false, reason: { kind: "no_draft" } };
  }

  const gate = derivePreSpendGate(job);
  if (!gate.cleared) return { ok: false, reason: { kind: "gate_blocked", gate } };

  const currentHash = computeBomHash(job);
  if (currentHash !== draft.sourceBomHash) {
    // Drift detected. Lawful only if a CO was signed after prep.
    const lastCo = getLastSignedChangeOrderAt(job);
    const lawful =
      lastCo &&
      lastCo > draft.preparedAt &&
      lastCo !== draft.lastSignedChangeOrderAtPrep;
    if (lawful) {
      return { ok: false, reason: { kind: "lawful_drift_change_order", changeOrderSignedAt: lastCo! } };
    }
    return {
      ok: false,
      reason: {
        kind: "integrity_violation",
        expectedHash: draft.sourceBomHash,
        actualHash: currentHash,
      },
    };
  }

  return { ok: true };
}

// ── Prep ──────────────────────────────────────────────────────────────────

export interface PrepInput {
  job: Job;
  vendorGroups: OrderDraftVendorGroup[];
  preparedBy: string;
  notes?: string;
  now?: Date;
}

export function buildPreparedDraft(input: PrepInput): OrderDraft {
  const now = input.now || new Date();
  return {
    draftId: `draft-${now.getTime().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    jobId: input.job.id,
    sourceBomHash: computeBomHash(input.job),
    sourceSpecRef: getSpecRef(input.job),
    vendorGroups: input.vendorGroups,
    notes: input.notes,
    preparedAt: now.toISOString(),
    preparedBy: input.preparedBy,
    status: "prepared",
    lastSignedChangeOrderAtPrep: getLastSignedChangeOrderAt(input.job),
  };
}

// ── Display helpers ───────────────────────────────────────────────────────

export function summarizeDraft(draft: OrderDraft): {
  vendorCount: number;
  itemCount: number;
  totalCost: number;
} {
  return {
    vendorCount: draft.vendorGroups.length,
    itemCount: draft.vendorGroups.reduce((s, g) => s + g.items.length, 0),
    totalCost: draft.vendorGroups.reduce((s, g) => s + g.totalCost, 0),
  };
}

export function describeBlockReason(reason: PushBlockReason): string {
  switch (reason.kind) {
    case "no_draft": return "No prepared draft for this job.";
    case "draft_voided":
      if (reason.reason === "change_order") return "Draft voided by Change Order — re-prep required.";
      if (reason.reason === "integrity_violation") return "Draft voided — BOM integrity violation. Re-prep required.";
      return "Draft voided.";
    case "gate_blocked": return reason.gate.shortReason;
    case "lawful_drift_change_order":
      return "Materials changed by signed Change Order — re-prep required.";
    case "integrity_violation":
      return "BOM changed without a Change Order — push blocked. Logged as integrity violation.";
  }
}
